/**
 * Float — wraps any children in a 3-variant ambient float container.
 *
 * Cycles className across `.float-a / .float-b / .float-c` per child
 * so siblings never sync. Requires float CSS keyframes (inline below via
 * a <style> tag injected once) or the companion 04-float-variants.css.
 *
 * Usage:
 *   <FloatGroup>
 *     <Card>...</Card>
 *     <Card>...</Card>
 *     <Card>...</Card>
 *   </FloatGroup>
 *
 * Optional: use `flat` to skip rotation (calmer feel for body cards).
 *
 * Provenance: paperclip/context/pattern-picker/COMPETITOR_REVIEW_TRYMAKO_2026-05-13.md §9f
 */
import { Children, ReactElement, cloneElement, isValidElement, ReactNode, useEffect } from "react";

const VARIANTS = ["a", "b", "c"] as const;

const FLOAT_CSS = `
@keyframes float-a { 0%,100%{transform:translateY(0) rotate(0deg)} 33%{transform:translateY(-8px) rotate(.6deg)} 66%{transform:translateY(-4px) rotate(-.4deg)} }
@keyframes float-b { 0%,100%{transform:translateY(0) rotate(0deg)} 33%{transform:translateY(-5px) rotate(-.5deg)} 66%{transform:translateY(-10px) rotate(.3deg)} }
@keyframes float-c { 0%,100%{transform:translateY(0) rotate(0deg)} 33%{transform:translateY(-10px) rotate(.4deg)} 66%{transform:translateY(-3px) rotate(-.6deg)} }
@keyframes float-flat-a { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
@keyframes float-flat-b { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
@keyframes float-flat-c { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
.float-a { animation: float-a 6s ease-in-out infinite; }
.float-b { animation: float-b 7s ease-in-out infinite; }
.float-c { animation: float-c 8s ease-in-out infinite; }
.float-flat-a { animation: float-flat-a 6s ease-in-out infinite; }
.float-flat-b { animation: float-flat-b 7s ease-in-out infinite; }
.float-flat-c { animation: float-flat-c 8s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .float-a,.float-b,.float-c,.float-flat-a,.float-flat-b,.float-flat-c { animation: none; }
}
`;

let injected = false;
function injectFloatCSS() {
  if (injected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.textContent = FLOAT_CSS;
  document.head.appendChild(style);
  injected = true;
}

export type FloatGroupProps = {
  children: ReactNode;
  /** If true, use floatX-flat (no rotation). Default false. */
  flat?: boolean;
};

export function FloatGroup({ children, flat = false }: FloatGroupProps) {
  useEffect(() => { injectFloatCSS(); }, []);

  return (
    <>
      {Children.map(children, (child, i) => {
        if (!isValidElement(child)) return child;
        const variant = VARIANTS[i % VARIANTS.length];
        const className = `${flat ? "float-flat-" : "float-"}${variant}`;
        const existing = (child.props as { className?: string }).className ?? "";
        return cloneElement(child as ReactElement<{ className?: string }>, {
          className: existing ? `${existing} ${className}` : className,
        });
      })}
    </>
  );
}
